// Shared FocusKey type + ladder helpers for the Command Center surface.
//
// Lives outside ``CommandCenterPage.tsx`` so the new G3.7 Phase 0
// primitives (PipelineStepper, PhaseLock, SkipGate, gateOptionality)
// can import the type without round-tripping through the page module
// — that round-trip would create a circular import because
// CommandCenterPage already imports the new primitives.
//
// Source for the union: ``frontend/src/pages/CommandCenterPage.tsx``
// FocusKey definition. Any new focus key MUST be added here first,
// then re-exported by the page.

export type FocusKey =
  | 'briefing'
  | 'gate1'
  | 'pipeline2'
  | 'pipeline3'
  | 'pipeline4'
  | 'pipeline5'
  | 'gate2'
  | 'gate25'
  | 'gate3'
  // Quality-Uplift Q7 (plan §7.1 step 4) — half-step Coherence Gate
  // between Phase 3 and Phase 4. Surfaces non-truncation citation
  // findings + Q5 dedup needs_review rows.
  | 'gate35'
  // Gate G3.7 Phase 6 (plan ``AI_ALM_Gate_G3_7_Implementation_Plan.md``
  // §8) — SRD Technical Depth Audit. Sits after G3.5 and before
  // Phase 4. Surfaces SRD Depth Probe findings as Approve / Edit /
  // Defer / Reject decisions per finding.
  | 'gate37'
  | 'gate4'
  | 'gate5'
  | 'artifacts'
  | 'inputs'
  | 'export'
  | 'approvalChain'
  // 2026-05-28 — Pre-Export Review surface for AI-governance
  // ratification. Same shape as ``approvalChain``: a Command Center
  // surface, not a phase / gate. Closes
  // PEG-AI-GOVERNANCE-SIGNATURE-001 post-hoc when the architect
  // didn't ratify at intake.
  | 'aiGovernance'

/**
 * The phase or gate number a focus key ultimately maps to (used by
 * the PipelineStepper for ordering + the PhaseLock cascade table).
 *
 * Values mirror the backend's ``Gate.gate_number`` Literal where
 * applicable (G2.5 → 25, G3.5 → 35, G3.7 → 37) so the FE and BE
 * stay in lock-step.
 */
export const FOCUS_TO_GATE_NUMBER: Record<FocusKey, number | null> = {
  briefing: 1, // briefing is *part of* Phase 1, not a gate
  gate1: 1,
  gate2: 2,
  gate25: 25,
  gate3: 3,
  gate35: 35,
  gate37: 37,
  gate4: 4,
  gate5: 5,
  pipeline2: null,
  pipeline3: null,
  pipeline4: null,
  pipeline5: null,
  artifacts: null,
  inputs: null,
  export: null,
  approvalChain: null,
  aiGovernance: null,
}

/**
 * Stable ordering used by the PipelineStepper to render pills in
 * left-to-right workflow order. Matches the user-visible cascade:
 * Phase 1 → G1 → Phase 2 → G2 → G2.5 → Phase 3 → G3 → G3.5 →
 * G3.7 → Phase 4 → G4 → Phase 5 → G5.
 */
export const STEPPER_ORDER: FocusKey[] = [
  'briefing',
  'gate1',
  'pipeline2',
  'gate2',
  'gate25',
  'pipeline3',
  'gate3',
  'gate35',
  'gate37',
  'pipeline4',
  'gate4',
  'pipeline5',
  'gate5',
  'export',
]

/**
 * Short human-readable label for the stepper pill (e.g. ``"P3"`` or
 * ``"G3.5"``). The PhaseLock tooltip uses the same labels so the
 * user gets consistent terminology across the rail and stepper.
 */
export const FOCUS_PILL_LABEL: Record<FocusKey, string> = {
  briefing: 'P1 Brief',
  gate1: 'G1',
  pipeline2: 'P2',
  gate2: 'G2',
  gate25: 'G2.5',
  pipeline3: 'P3',
  gate3: 'G3',
  gate35: 'G3.5',
  gate37: 'G3.7',
  pipeline4: 'P4',
  gate4: 'G4',
  pipeline5: 'P5',
  gate5: 'G5',
  export: 'Export',
  artifacts: 'Files',
  inputs: 'Inputs',
  approvalChain: 'Approval',
  aiGovernance: 'AI Gov',
}
